import React, { useState, useCallback, useEffect, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import { useHistory } from 'react-router-dom';
import { useSelector, useDispatch } from 'react-redux';
import { useLazyQuery } from '@apollo/client';
import { ControlBarButton } from '@m12s/component-library';
import { Box } from '@chakra-ui/react';
import { useEmbeddedEffect } from '@machinemetrics/mm-react-embeddable';
import { check, signOut, playRegular, user } from 'lib/icons';
import { UtilityMenu } from 'components/UtilityMenu';
import useOperatorLogout from 'components/LaborTicket/hooks/useOperatorLogout';
import useSelectOperator from 'lib/hooks/useSelectOperator';
import MMEmbeddableZoneWithLoader from 'components/MMEmbeddableZoneWithLoader';
import MMDrawer from 'components/MMDrawer';
import { getMachine } from 'lib/selectors/getMachine';
import { getScopeStart } from 'lib/selectors/getScopeStart';
import { getLatestOperator } from 'lib/selectors/getLatestOperator';
import { getCurrentOperator } from 'lib/selectors/getCurrentOperator';
import { LABOR_TICKET_HOST } from 'lib/appConfig';
import { LABOR_TICKET_ACTIONS } from 'lib/constants';
import { CHECK_OPEN_LABOR_TICKETS } from 'lib/api/queries';
import {
  actionOpenLaborTicketModal,
  actionSetEmbeddedLaborTicket,
} from 'lib/actions';
import { MultiJobSelectFlow } from 'components/MultiJobSelect';
import { getHasCards } from 'lib/selectors/getHasCards';
import { CategorizeDowntime } from './ERPJobControlBar/CategorizeDowntime';
import {
  ControlBarResponsive,
  MobileIcon,
  ResponsiveText,
  Scrollable,
  SingleButtonScrollable,
} from './styled';

/**
 * Control bar for APM multi-job (palletized) machines.
 *
 * Shows "Submit all" when there are program events or open labor tickets,
 * otherwise shows "Select Jobs". Categorize and Sign out always appear.
 */
const ERPMultiJobControlBar = () => {
  const { t } = useTranslation();
  const history = useHistory();
  const machine = useSelector(getMachine);
  const scopeStart = useSelector(getScopeStart);
  const latestOperator = useSelector(getLatestOperator);
  const latestOperatorErpId = latestOperator?.erpId;
  const operator = useSelector(getCurrentOperator);
  const dispatch = useDispatch();
  const requestOperatorLogout = useOperatorLogout();
  const { openSelectOperator } = useSelectOperator();

  const hasCardsFromData = useSelector(getHasCards);
  const hasOpenLaborTickets = useSelector((state) => {
    return state.workOrderManagement.hasOpenLaborTickets;
  });
  const [
    checkOpenLTs,
    checkOpenLTsResult,
  ] = useLazyQuery(CHECK_OPEN_LABOR_TICKETS, { fetchPolicy: 'network-only' });
  const [pendingSignOut, setPendingSignOut] = useState(false);
  const [forceNoCards, setForceNoCards] = useState(false);
  const showSubmitAll = hasCardsFromData && !forceNoCards;

  useEffect(() => {
    if (!hasCardsFromData) setForceNoCards(false);
  }, [hasCardsFromData]);

  useEffect(() => {
    if (
      !pendingSignOut ||
      checkOpenLTsResult.loading ||
      !checkOpenLTsResult.called
    )
      return;
    setPendingSignOut(false);
    if (checkOpenLTsResult.data?.erpLaborTickets?.length > 0) {
      setDismissedItemIds([]);
      setSubmitAllOpenedFromSignOut(true);
      setSubmitAllOpen(true);
    } else {
      requestOperatorLogout();
    }
  }, [pendingSignOut, checkOpenLTsResult, requestOperatorLogout]);

  const [submitAllOpen, setSubmitAllOpen] = useState(false);
  const [selectJobsOpen, setSelectJobsOpen] = useState(false);
  const [openedFromSubmitAll, setOpenedFromSubmitAll] = useState(false);
  const [eventFormOpen, setEventFormOpen] = useState(false);
  const [eventFormContext, setEventFormContext] = useState(null);
  const [submitAllRefreshToken, setSubmitAllRefreshToken] = useState(0);
  /** True only when Submit-all was opened via Sign out (not the Submit all bar button). */
  const [submitAllOpenedFromSignOut, setSubmitAllOpenedFromSignOut] = useState(
    false
  );
  const pendingItemCountRef = useRef(0);
  const pendingDismissIdRef = useRef(null);
  const [dismissedItemIds, setDismissedItemIds] = useState([]);

  // Add Job → open single-select job drawer (no navigation)
  useEffect(() => {
    const handler = () => {
      window.dispatchEvent(new CustomEvent('apm:open-select-job-drawer'));
    };
    window.addEventListener('apm:add-job', handler);
    return () => {
      return window.removeEventListener('apm:add-job', handler);
    };
  }, []);

  // Assign unknown job (e.g. UnknownJob footer) → same apm-select-jobs drawer as "Select Jobs"
  useEffect(() => {
    const handler = () => {
      setSelectJobsOpen(true);
    };
    window.addEventListener('apm:open-apm-select-jobs', handler);
    return () => {
      window.removeEventListener('apm:open-apm-select-jobs', handler);
    };
  }, []);

  const closeSubmitAll = useCallback(() => {
    setSubmitAllOpen(false);
  }, []);
  const closeSelectJobs = useCallback(() => {
    setSelectJobsOpen(false);
  }, []);
  const handleReturnFromForm = useCallback(
    (reopenSubmitAll, didSubmit = false, submitResponse = null) => {
      if (openedFromSubmitAll) {
        setOpenedFromSubmitAll(false);
        if (reopenSubmitAll) {
          if (didSubmit) {
            const dismissId = pendingDismissIdRef.current;
            if (dismissId) {
              setDismissedItemIds((prev) => {
                return [...prev, dismissId];
              });
              pendingDismissIdRef.current = null;
            }
            setSubmitAllRefreshToken((token) => {
              return token + 1;
            });
            window.dispatchEvent(
              new CustomEvent('apm:opdash-refresh', {
                detail: { dismissItemId: dismissId },
              })
            );
            const createdFollowOn =
              submitResponse?.actionResults?.createProductionTicket ||
              submitResponse?.createProductionTicket;
            if (pendingItemCountRef.current > 1) {
              setSubmitAllOpen(true);
            } else if (!createdFollowOn) {
              setForceNoCards(true);
            }
          } else {
            setSubmitAllOpen(true);
          }
        }
      }
    },
    [openedFromSubmitAll]
  );

  const handleEventFormClose = useCallback(() => {
    setEventFormOpen(false);
    setEventFormContext(null);
    handleReturnFromForm(true, false);
  }, [handleReturnFromForm]);

  const handleEventFormSubmit = useCallback(
    (data) => {
      setEventFormOpen(false);
      setEventFormContext(null);
      handleReturnFromForm(true, true, data);
    },
    [handleReturnFromForm]
  );

  useEmbeddedEffect(
    'labor-ticket-form-submit-all',
    'labor-ticket-form:close',
    closeSubmitAll
  );
  useEmbeddedEffect(
    'labor-ticket-form-submit-all',
    'labor-ticket-screen:close',
    closeSubmitAll
  );

  // When a ticket row is clicked, close submit-all drawer and open the appropriate form
  useEmbeddedEffect(
    'labor-ticket-form-submit-all',
    'labor-ticket-form-submit-all:open-ticket',
    useCallback(
      (data) => {
        pendingItemCountRef.current = data?.itemCount || 0;
        pendingDismissIdRef.current = data?.eventId
          ? `event-${data.eventId}`
          : data?.laborTicketRef
          ? `lt-${data.laborTicketRef}`
          : null;

        const laborTicketAction =
          data.isSignOut && data.itemCount === 1
            ? LABOR_TICKET_ACTIONS.OPERATOR_LOGOUT
            : 'submit';

        if (data?.laborTicketRef) {
          setSubmitAllOpen(false);
          setOpenedFromSubmitAll(true);
          dispatch(
            actionSetEmbeddedLaborTicket({
              laborTicketRef: data.laborTicketRef,
              personId: data.personId || latestOperatorErpId,
              workOrderId: data.workOrderId,
              sequenceNumber: data.sequenceNumber,
              resourceId: data.resourceId,
            })
          );
          dispatch(
            actionOpenLaborTicketModal(laborTicketAction, {
              isSignOut: !!data.isSignOut,
            })
          );
        } else if (data?.eventId && data?.machineRef) {
          // Program event without a labor ticket — open the event form
          setSubmitAllOpen(false);
          setOpenedFromSubmitAll(true);
          setEventFormContext({
            eventId: data.eventId,
            eventRefs: data.eventRefs,
            machineRef: data.machineRef,
            orderNumber: data.workOrderId,
            partNumber: data.partNumber,
            sequenceNumber: data.sequenceNumber,
            clockIn: data.clockIn,
            personId: data.personId || latestOperatorErpId,
            workOrderOperationRef: data.workOrderOperationRef,
            type: data.ticketType || 'production',
            isAPMControlled: true,
            opened: true,
            action: laborTicketAction,
            isSignOut: !!data.isSignOut,
          });
          setEventFormOpen(true);
        }
      },
      [dispatch, latestOperatorErpId]
    )
  );

  const onLaborTicketClose = useCallback(() => {
    handleReturnFromForm(true, false);
  }, [handleReturnFromForm]);
  const onLaborTicketSubmit = useCallback(
    (data) => {
      handleReturnFromForm(true, true, data);
    },
    [handleReturnFromForm]
  );
  useEmbeddedEffect(
    'labor-ticket-form',
    'labor-ticket-form:close',
    onLaborTicketClose
  );
  useEmbeddedEffect(
    'labor-ticket-form',
    'labor-ticket-form:submit',
    onLaborTicketSubmit
  );
  useEmbeddedEffect(
    'labor-ticket-event-form',
    'labor-ticket-form:close',
    handleEventFormClose
  );
  useEmbeddedEffect(
    'labor-ticket-event-form',
    'labor-ticket-form:submit',
    handleEventFormSubmit
  );

  if (!operator) {
    return (
      <>
        <ControlBarResponsive id="job-control-bar">
          <UtilityMenu />
          <SingleButtonScrollable>
            <ControlBarButton
              id="job-control-bar-sign-in"
              onClick={() => {
                openSelectOperator();
              }}
            >
              <MobileIcon icon={user} />
              <ResponsiveText
                full={t('signIn|full')}
                medium={t('signIn|medium')}
                short={t('signIn|short')}
              />
            </ControlBarButton>
          </SingleButtonScrollable>
        </ControlBarResponsive>
      </>
    );
  }

  return (
    <>
      <ControlBarResponsive id="job-control-bar">
        <UtilityMenu />
        <Scrollable>
          {showSubmitAll ? (
            <ControlBarButton
              id="job-control-submit-all"
              onClick={() => {
                setDismissedItemIds([]);
                setSubmitAllOpenedFromSignOut(false);
                setSubmitAllOpen(true);
              }}
            >
              <MobileIcon icon={check} />
              <ResponsiveText
                full="Submit all"
                medium="Submit all"
                short="Submit"
              />
            </ControlBarButton>
          ) : (
            <ControlBarButton
              id="job-control-select-jobs"
              onClick={() => {
                setSelectJobsOpen(true);
              }}
            >
              <MobileIcon icon={playRegular} />
              <ResponsiveText
                full="Select Jobs"
                medium="Select Jobs"
                short="Jobs"
              />
            </ControlBarButton>
          )}

          <CategorizeDowntime history={history} machine={machine} />

          <ControlBarButton
            id="signout"
            onClick={() => {
              if (hasOpenLaborTickets) {
                setDismissedItemIds([]);
                setSubmitAllOpenedFromSignOut(true);
                setSubmitAllOpen(true);
                return;
              }
              checkOpenLTs({
                variables: {
                  where: {
                    _and: [
                      { machineRef: { _eq: machine?.machineRef } },
                      { clockOut: { _is_null: true } },
                      { personId: { _eq: latestOperatorErpId } },
                    ],
                  },
                },
              });
              setPendingSignOut(true);
            }}
          >
            <MobileIcon icon={signOut} />
            <ResponsiveText
              full={t('signout|full')}
              medium={t('signout|medium')}
              short={t('signout|short')}
            />
          </ControlBarButton>
        </Scrollable>
      </ControlBarResponsive>

      {/* Submit All drawer */}
      <MMDrawer
        isOpen={submitAllOpen}
        onClose={closeSubmitAll}
        title="Submit all work"
      >
        <MMEmbeddableZoneWithLoader
          id="labor-ticket-form-submit-all"
          src={LABOR_TICKET_HOST}
          path="/submit-all"
          context={{
            machineRef: machine?.machineRef,
            start: scopeStart,
            erpResources: machine?.erpResources,
            refreshToken: submitAllRefreshToken,
            dismissedItemIds,
            personId: latestOperatorErpId,
            isSignOut: submitAllOpenedFromSignOut,
          }}
        />
      </MMDrawer>

      {/* Select Jobs drawer (multi-select) — apm-select-jobs MFE */}
      <MMDrawer
        isOpen={selectJobsOpen}
        onClose={closeSelectJobs}
        title="Select Jobs"
        hideHeader
      >
        <Box
          flex={1}
          minH={0}
          display="flex"
          flexDirection="column"
          overflow="hidden"
          h="calc(100vh - 2rem)"
        >
          <MultiJobSelectFlow
            machineRef={machine?.machineRef}
            machineId={machine?.id}
            erpResources={machine?.erpResources}
            onCancel={closeSelectJobs}
            onComplete={closeSelectJobs}
          />
        </Box>
      </MMDrawer>

      {/* Program event form drawer */}
      <MMDrawer
        isOpen={eventFormOpen}
        onClose={handleEventFormClose}
        title="Submit work"
      >
        {eventFormContext && (
          <MMEmbeddableZoneWithLoader
            id="labor-ticket-event-form"
            src={LABOR_TICKET_HOST}
            path={`/program-event/${eventFormContext.machineRef}/${eventFormContext.eventId}`}
            context={eventFormContext}
          />
        )}
      </MMDrawer>
    </>
  );
};

export { ERPMultiJobControlBar };
